import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import logoDefender from '../../../../assets/images/logo-defender.svg'
import {
  escapeHtml,
  renderExportTableCellHtml,
  buildReportHeaderHtml,
  buildFiltersBarHtml,
  buildUserInfoHtml,
  buildTotalsBarHtml,
  buildFlowHtml,
  buildContextCardHtml,
  buildContextColapseHtml,
  buildContextRowHtml,
  buildContextQrCodeScript,
} from './core'

// ========== VERSÃO OFICIAL PDF HTML ==========
export const exportGridToPDF_HTML_OFFICIAL = async (options = {}, onProgress) => {
  const filename = options.filename || 'relatorio_html_v2.pdf'
  const title = options.title || 'Relatório de Dados'
  const includeFACss = options.includeFontAwesomeCss !== false
  const faCssUrl = options.faCssUrl || 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css'
  const maxCellWidthPx = typeof options.maxCellWidthPx === 'number' ? options.maxCellWidthPx : 100
  const reportCssUrl = options.reportCssUrl || (typeof window !== 'undefined' ? `${window.location.origin}/report-export.css` : '/report-export.css')
  const includeAppCss = options.includeAppCss !== false
  const includeGoogleFontsCss = options.includeGoogleFontsCss !== false
  const googleFontsUrl = options.googleFontsUrl || 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap'
  const resolvedLogoUrl = options.logoUrl || (typeof window !== 'undefined' ? new URL(logoDefender, window.location.origin).href : logoDefender)
  
  // Configuração mais agressiva para maximizar linhas por página
  const estimatedRowHeight = 35 // Altura estimada menor para mais linhas
  const headerFooterSpace = 200 // Reduzir espaço reservado para header
  const availableHeight = 1200 - headerFooterSpace // Altura disponível para tabela
  const rowsPerPage = options.rowsPerPage || Math.floor(availableHeight / estimatedRowHeight) // Aproximadamente 28-30 linhas
  
  // Etapa 1: Preparar dados (10%)
  onProgress?.(10, "Preparando dados...");
  
  const appCssLinks = includeAppCss && typeof window !== 'undefined'
    ? (() => {
        try {
          const hrefs = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
            .map(l => l && l.href)
            .filter(Boolean)
          const unique = Array.from(new Set(hrefs))
          const linksStr = unique
            .map(href => { try { return new URL(href, window.location.origin).href } catch { return href } })
            .map(abs => `<link rel="stylesheet" href="${abs}" />`)
            .join('\n')
          const styleTags = Array.from(document.querySelectorAll('style'))
            .map(s => s && s.textContent)
            .filter(css => css && css.trim().length > 0)
          const inlineStr = styleTags.length ? `<style>\n${styleTags.join('\n\n')}\n</style>` : ''
          return `${linksStr}\n${inlineStr}`
        } catch { return '' }
      })()
    : ''

  const columnsList = options.columns || [];
  const dataResult = options.filteredData || [];

  const exportColumns = columnsList.filter((col) =>
    (col.Export !== 'False' && col.Export !== false)
  );

  // Dividir dados em páginas
  const paginateData = (data, rowsPerPage) => {
    const pages = []
    for (let i = 0; i < data.length; i += rowsPerPage) {
      pages.push(data.slice(i, i + rowsPerPage))
    }
    return pages.length > 0 ? pages : [[]]
  }
  
  // Etapa 2: Processar dados (30%)
  onProgress?.(30, "Processando dados...");
  
  const dataPages = paginateData(dataResult, rowsPerPage)
  const totalPages = dataPages.length

  const headers = exportColumns.map((c) => {
    const widthStyle = c.width ? ` style="width: ${c.width};"` : '';
    return `<th${widthStyle}>${escapeHtml(c.header)}</th>`;
  }).join('')

  const headerHtml = buildReportHeaderHtml({ title, logoUrl: resolvedLogoUrl })

  const filtersBarHtml = buildFiltersBarHtml({ appliedFilters: options.appliedFilters, transactionCode: options.transactionCode || '' })

  const { html: userInfoHtml, userInfo } = buildUserInfoHtml(options)

  const flowHtml = buildFlowHtml(options.flowData)
  const contextCardHtml = buildContextCardHtml(options.contextData)
  const { html: contextColapseHtml, qrCodeItems: contextQrCodeItems } = buildContextColapseHtml(
    options.contextColapseData,
    options.enableContextColapseQRCode === true,
  )
  const contextQrCodeScript = buildContextQrCodeScript(contextQrCodeItems)
  const contextRowHtml = buildContextRowHtml({ flowHtml, contextCardHtml, contextColapseHtml })

  const totalsBarHtml = buildTotalsBarHtml(dataResult.length)

  // Preparar dados do footer
  const d = new Date(); const pad = (n) => String(n).padStart(2, '0')
  const stamp = `${pad(d.getDate())}/${pad(d.getMonth()+1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`

  const nome = userInfo?.nome || ''

  // Etapa 3: Gerar PDF (50%)
  onProgress?.(50, "Gerando PDF...");
  
  // Gerar PDFs para cada página separadamente
  const pdf = new jsPDF('p', 'mm', 'a4')
  let isFirstPage = true

  for (let pageIndex = 0; pageIndex < dataPages.length; pageIndex++) {
    // Progresso mais suave por página (50% a 85%)
    const pageProgress = 50 + (pageIndex / dataPages.length) * 35;
    onProgress?.(Math.round(pageProgress), `Processando página ${pageIndex + 1} de ${dataPages.length}`);
    
    if (!isFirstPage) pdf.addPage()
    
    const pageData = dataPages[pageIndex]
    const pageNumber = pageIndex + 1
    const rows = pageData.map((row) => {
      const tds = exportColumns.map((col) => {
        const content = renderExportTableCellHtml(row[col.field], col)
        const widthStyle = col.width ? ` style="width: ${col.width};"` : '';
        return `<td class="cell"${widthStyle}>${content}</td>`
      }).join('')
      return `<tr>${tds}</tr>`
    }).join('\n')

    // Componentes específicos da página
    const currentUserInfoHtml = pageNumber === 1 ? userInfoHtml : ''
    const currentFiltersBarHtml = pageNumber === 1 ? filtersBarHtml : ''
    const currentContextRowHtml = pageNumber === 1 ? contextRowHtml : ''
    const currentContextQrCodeScript = pageNumber === 1 ? contextQrCodeScript : ''
    const currentTotalsBarHtml = pageNumber === totalPages ? totalsBarHtml : ''

    const pageHtml = `<!DOCTYPE html>
    <html lang="pt-BR">
    <head>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <title>${escapeHtml(title)}</title>
      <style>
        :root{--cell-max-width: ${maxCellWidthPx}px;}
        body { padding-bottom: 10px; margin: 0; font-family: "Poppins", sans-serif; }
        .main-content { padding: 0px 20px 5px 20px !important; }
        table { table-layout: fixed !important; width: 100% !important; }
        table th, table td { padding: 10px 8px !important; font-size: 12px !important; overflow: hidden !important; word-wrap: break-word !important; white-space: normal !important; }
        td.cell { padding-top: 10px !important; padding-bottom: 10px !important; }
        .defender-header { padding: 8px 20px !important; }
        .filters-bar { padding: 5px 20px !important; }
        .user-info-grid { padding: 8px 20px !important; margin: 0px 20px !important; }
        .totals-bar { margin: 8px 20px !important; }
      </style>
      <link rel="stylesheet" href="${reportCssUrl}" />
      ${includeGoogleFontsCss ? `<link rel="stylesheet" href="${googleFontsUrl}" />` : ''}
      ${includeFACss ? `<link rel="stylesheet" href="${faCssUrl}" crossorigin="anonymous" referrerpolicy="no-referrer" />` : ''}
      ${appCssLinks}
    </head>
    <body>
      ${headerHtml}
      ${currentFiltersBarHtml}
      ${currentUserInfoHtml}
      <div class="main-content">
        ${currentContextRowHtml}
        <table>
          <thead><tr>${headers}</tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      ${currentTotalsBarHtml}
      ${currentContextQrCodeScript}
    </body>
    </html>`

    // Calcular altura dinâmica baseada no conteúdo da página
    const baseHeight = 250 // Reduzir altura base para dar mais espaço à tabela
    const tableHeight = pageData.length * estimatedRowHeight + 80 // Linhas + header da tabela
    const dynamicHeight = Math.max(600, baseHeight + tableHeight)
    
    // Criar iframe para esta página
    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.left = '0'
    iframe.style.top = '0'
    iframe.style.width = '1024px'
    iframe.style.height = `${dynamicHeight}px`
    iframe.style.opacity = '0'
    iframe.style.pointerEvents = 'none'
    document.body.appendChild(iframe)
    
    const idoc = iframe.contentDocument || iframe.contentWindow.document
    idoc.open(); idoc.write(pageHtml); idoc.close()
    await new Promise(r => setTimeout(r, 100))
    try { if (idoc.fonts && idoc.fonts.ready) await idoc.fonts.ready } catch(_) {}

    // Aguarda a renderização assíncrona dos QR Codes do bloco de contexto expansivo (página 1)
    if (pageNumber === 1 && contextQrCodeItems.length > 0) {
      const qrTimeoutMs = 2000
      const qrStart = Date.now()
      while (Date.now() - qrStart < qrTimeoutMs) {
        const allRendered = contextQrCodeItems.every((item) => {
          const el = idoc.getElementById(item.id)
          return el && el.children && el.children.length > 0
        })
        if (allRendered) break
        await new Promise((r) => setTimeout(r, 100))
      }
    }

    // Etapa 4: Renderizar página (progresso mais suave)
    const renderProgress = 50 + ((pageIndex + 0.5) / dataPages.length) * 35;
    onProgress?.(Math.round(renderProgress), `Processando página ${pageIndex + 1} de ${dataPages.length}`);

    const pageZoom = typeof options.zoom === 'number' && options.zoom > 0 && options.zoom <= 1 ? options.zoom : 0.9
    const canvas = await html2canvas(idoc.body, { 
      scale: 2,
      useCORS: true, 
      allowTaint: true, 
      windowWidth: Math.round(1024/pageZoom), 
      backgroundColor: '#ffffff' 
    })
    
    const imgData = canvas.toDataURL('image/jpeg', 0.92)
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    
    const margin = 10
    const pdfFooterHeight = 15
    const footerMargin = 5
    const imgWidth = pageWidth - margin * 2
    const imgHeight = canvas.height * imgWidth / canvas.width
    
    // Adicionar imagem da página
    pdf.addImage(imgData, 'JPEG', margin, margin, imgWidth, imgHeight)
    
    // Adicionar rodapé mais próximo do conteúdo
    const footerText = `Emitido por: ${nome} às ${stamp}`
    const pageText = `Página ${pageNumber} de ${totalPages}`
    
    // Posicionar rodapé logo após o conteúdo, mas respeitando margem mínima
    const contentBottom = margin + imgHeight
    const minFooterY = pageHeight - pdfFooterHeight - footerMargin
    const footerY = Math.min(contentBottom + 5, minFooterY) // 5mm de espaço após conteúdo
    
    pdf.setFontSize(9)
    pdf.setTextColor(255, 255, 255)
    pdf.setFillColor(36, 78, 162)
    pdf.rect(0, footerY, pageWidth, pdfFooterHeight, 'F')
    
    const textY = footerY + (pdfFooterHeight / 2) + 2
    pdf.text(footerText, 10, textY)
    pdf.text(pageText, pageWidth - 10, textY, { align: 'right' })

    // Limpar iframe
    try { document.body.removeChild(iframe) } catch (_) {}
    
    isFirstPage = false
  }

  // Etapa 5: Finalizar PDF (95% a 100%)
  onProgress?.(95, "Finalizando PDF...");
  await new Promise(resolve => setTimeout(resolve, 200)); // Pequena pausa para visualizar
  onProgress?.(100, "PDF pronto!");

  pdf.save(filename)
}

// Exportar a versão oficial como principal
export { exportGridToPDF_HTML_OFFICIAL as exportGridToPDF_HTML }
export default exportGridToPDF_HTML_OFFICIAL


